import React, { useState, useEffect } from 'react'
import styled, { css } from 'styled-components'
import PropTypes from 'prop-types'

import { color, spacing, border, fontSize, breakpoint } from '../theme'

import { Avatar } from '../atoms/Avatar'
import Chevron from '../atoms/Chevron'
import { ChildWithTimeSelect } from '../molecules/ChildWithTimeSelect'
import { Table, TableHeader } from '../molecules/Table'
import { H6 } from '../atoms/Heading'

const DayWrapper = styled.div`
   border: 2px solid ${color.primary};
   border-radius: ${border.radius};
   margin-bottom: ${spacing.m};

   ${props =>
      props.isDisabled
         ? css`
              background: ${color.light};
              border: 2px solid ${color.light};
              color: ${color.disabledText};
              pointer-events: none;
              * {
                 color: inherit;
              }
           `
         : css``}
`

const Header = styled.button`
   width: 100%;
   border: none;
   background: none;
   font-family: inherit;
   display: flex;
   grid-template-columns: 1fr 2fr 1fr;
   align-items: center;
   justify-content: space-between;
   cursor: pointer;
   padding: ${spacing.m} ${spacing.ml};
   height: 80px;

   h6 {
      text-align: left;
      margin-bottom: 0;
      width: 25%;
   }

   p {
      margin: 0;
      font-size: ${fontSize.sm};
   }
   @media screen and (min-width: ${breakpoint.s}) {
      h6,
      p {
         font-size: ${fontSize.ml};
      }
   }
`

const AvatarContainer = styled.div`
   flex-grow: 1;
   display: flex;
   align-items: center;
   justify-content: center;
   & > div {
      margin-left: -0.5rem;
   }
`

const ExpandedContent = styled.div`
   margin: 0 ${spacing.ml};
   padding: ${spacing.m} 0;
   border-top: 2px solid ${color.borderLight};
   display: ${props => (props.expanded ? 'block' : 'none')};
`

const LastColumn = styled.div`
   display: flex;
   align-items: center;
   justify-content: flex-end;
   width: 25%;

   & > * {
      margin-left: ${spacing.sm};
   }
`

const DateContainer = styled.p``

const DisabledText = styled.p`
   color: ${color.blueDark};
   flex-grow: 1;
   text-align: center;
`

export const ExpandableDate = ({ day, children, onChange, isExpanded }) => {
   const [expanded, setExpanded] = useState(isExpanded)
   const [bookedChildren, setBookedChildren] = useState([])
   const [currentChild, setCurrentChild] = useState(null)

   useEffect(() => {
      if (typeof onChange === 'function' && currentChild) {
         onChange(currentChild)
      }
   }, [currentChild])

   /**
    * When checkbox is changed:
    * if checked add to bookedChildren
    * else remove from bookedChildren
    *
    * @param event
    */
   const pickChild = (picked, value) => {
      if (value.child) {
         const array = [...bookedChildren]

         if (picked) {
            const index = getObjectPositionById(value, array)

            if (index === -1) {
               array.push(value)
            } else {
               array[index] = value
            }
            setBookedChildren(array)
         } else {
            removeBooking(value, array)
         }
         setCurrentChild(value)
      }
   }

   /**
    * Remove child from booked children.
    * reset Booked children
    *
    * @param value
    */
   const removeBooking = (value, array) => {
      const index = getObjectPositionById(value, array)

      if (index !== -1) {
         array.splice(index, 1)
         setBookedChildren(array)
      }
   }

   /**
    * check if id of object matches child id in array and return the positition
    * @param object
    * @param array
    * @return {*}
    */
   const getObjectPositionById = (object, array) => {
      if (array.length > 0) {
         const idExists = obj => obj.child.id === object.child.id
         return array.findIndex(idExists)
      } else return -1
   }

   const findChild = childId => {
      return children.filter((child, key) => {
         /* Add child order to child object */
         child['order'] = child.childOrder ? child.childOrder : key + 1
         return child.id === childId ? child : null
      })[0]
   }

   const getNameFromDate = day => {
      const dateObj = new Date(day)

      return {
         weekdayName: dateObj.toLocaleString('en-GB', { weekday: 'long' }),
         monthName: dateObj.toLocaleString('en-GB', { month: 'short' }),
         date: dateObj.getDate()
      }
   }

   const dateNames = getNameFromDate(day.date)

   return (
      <>
         <DayWrapper isDisabled={day.schedules ? false : true}>
            <Header
               onClick={e => setExpanded(!expanded)}
               disabled={day.schedules ? false : true}
            >
               <H6>{dateNames.weekdayName}</H6>

               {!day.schedules && (
                  <DisabledText>No available booking</DisabledText>
               )}

               {bookedChildren && day.schedules ? (
                  <AvatarContainer>
                     {bookedChildren.map((booked, key) => {
                        return (
                           <Avatar
                              name={booked.child.name}
                              size={'medium'}
                              key={key}
                              childOrder={booked.child.order}
                           />
                        )
                     })}
                  </AvatarContainer>
               ) : (
                  ''
               )}

               <LastColumn>
                  <DateContainer>
                     {dateNames.monthName} {dateNames.date}
                  </DateContainer>
                  {day.schedules && (
                     <Chevron direction={expanded ? 'up' : 'down'} />
                  )}
               </LastColumn>
            </Header>
            {day.schedules && (
               <ExpandedContent expanded={expanded}>
                  <Table>
                     <TableHeader>
                        <p>Day & Child</p>
                        <p>Drop off</p>
                        <p>Pick up</p>
                     </TableHeader>

                     {day.schedules.map((child, key) => {
                        return child ? (
                           <ChildWithTimeSelect
                              child={findChild(child.id)}
                              key={key}
                              pickUp={
                                 child.pickUp
                                    ? child.pickUp
                                    : child.pickUpDefault
                              }
                              dropOff={
                                 child.dropOff
                                    ? child.dropOff
                                    : child.dropOffDefault
                              }
                              isChecked={child.checked ? child.checked : false}
                              onChange={values => {
                                 pickChild(values.picked, values)
                              }}
                           />
                        ) : null
                     })}
                  </Table>
               </ExpandedContent>
            )}
         </DayWrapper>
      </>
   )
}

ExpandableDate.propTypes = {
   /**
    * Day object
    */
   day: PropTypes.object.isRequired,
   /**
    * Default drop off time, string '07:00'
    */
   defaultDropOff: PropTypes.string,
   /**
    * Default pick up time, string '15:00'
    */
   defaultPickUp: PropTypes.string,
   /**
    * Array with children
    */
   children: PropTypes.array.isRequired,
   /**
    *  On Change event - gets booked children
    */
   onChange: PropTypes.func,
   /**
    *  On Change event - gets booked children
    */
   isExpanded: PropTypes.bool
}

ExpandableDate.defaultProps = {
   day: {},
   defaultDropOff: '07:00',
   defaultPickUp: '15:00',
   children: [],
   onChange: undefined,
   isExpanded: false
}
